/**
 * OrderKitchenSection: the "Kitchen" tab of an order (ADR 0027): each send to the kitchen with where every line
 * went (item to station and why), a warning when lines had no route, and the tickets with their state.
 *
 * Props: `orderId`, `mode` (the order screen's mode; print state is shown at the store only). Render it only when the
 * session has the portal's kitchen read permission (`canReadKitchen`). An order that was never sent, or that HO holds
 * no tickets for, shows an EmptyState, never an error.
 */
import { InlineNotification, Link as CarbonLink, SkeletonText, Table, TableBody, TableCell, TableContainer, TableHead, TableHeader, TableRow, Tag } from '@carbon/react';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { EmptyState } from '../components/EmptyState';
import { useFormat } from '../orders/model';
import { asProblem, problemMessage } from '../problems';
import { KitchenStatusTag } from './KitchenStatusTag';
import { KITCHEN_POLL_MS, kitchenKey, useKitchenApi, type KitchenMode } from './model';
import { PrintStatus } from './KitchenTicketsPage';
import './kitchen.scss';

export function OrderKitchenSection({ orderId, mode }: { orderId: string; mode: KitchenMode }) {
  const { t } = useTranslation();
  const api = useKitchenApi();
  const format = useFormat();
  const query = useQuery({
    queryKey: [...kitchenKey, 'order', orderId],
    queryFn: () => api.orderTickets(orderId),
    refetchInterval: mode === 'store' ? KITCHEN_POLL_MS : false,
    refetchIntervalInBackground: false,
    retry: false,
  });
  const problem = asProblem(query.error);
  const missing = problem?.status === 404;

  if (query.isPending) return <SkeletonText paragraph lineCount={4} />;
  if (query.isError && !missing) {
    return <InlineNotification kind="error" role="alert" lowContrast hideCloseButton title={problemMessage(t, problem)} />;
  }
  const data = query.data;
  if (!data || data.sends.length === 0) {
    return <EmptyState headingLevel={3} heading={t('admin.kitchen.order.empty')} description={t('admin.kitchen.order.emptyHint')} />;
  }
  const unrouted = data.sends.reduce((sum, s) => sum + Number(s.unroutedCount), 0);
  const sends = [...data.sends].sort((a, b) => a.sentAt.localeCompare(b.sentAt));

  return (
    <div className="pos-kitchen__order">
      {unrouted > 0 && (
        <InlineNotification
          kind="warning"
          role="status"
          lowContrast
          hideCloseButton
          title={t('admin.kitchen.order.unrouted', { count: unrouted })}
          subtitle={t('admin.kitchen.order.unroutedHint')}
        />
      )}
      {sends.map((send, index) => (
        <div key={send.id}>
          <h3 className="pos-kitchen__section-title">
            {t('admin.kitchen.order.send', { number: index + 1, time: format.dateTime(send.sentAt) })}{' '}
            {send.isAddition && <Tag type="gray">{t('admin.kitchen.addition')}</Tag>}
          </h3>
          <TableContainer>
            <Table aria-label={t('admin.kitchen.order.send', { number: index + 1, time: format.dateTime(send.sentAt) })}>
              <TableHead>
                <TableRow>
                  <TableHeader>{t('admin.kitchen.detail.item')}</TableHeader>
                  <TableHeader>{t('admin.kitchen.order.station')}</TableHeader>
                  <TableHeader>{t('admin.kitchen.routing.reason')}</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {send.routing.map((line) => (
                  <TableRow key={line.lineId}>
                    <TableCell>{line.name}</TableCell>
                    <TableCell>{line.stations.length > 0 ? line.stations.join(', ') : t('admin.kitchen.routing.nowhere')}</TableCell>
                    <TableCell>
                      {t(`admin.kitchen.routing.source.${line.source}` as I18nKey, { defaultValue: line.source })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      ))}

      <h3 className="pos-kitchen__section-title">{t('admin.kitchen.order.tickets')}</h3>
      <TableContainer>
        <Table aria-label={t('admin.kitchen.order.tickets')}>
          <TableHead>
            <TableRow>
              <TableHeader>{t('admin.kitchen.columns.ticket')}</TableHeader>
              <TableHeader>{t('admin.kitchen.columns.status')}</TableHeader>
              {mode === 'store' && <TableHeader>{t('admin.kitchen.columns.printStatus')}</TableHeader>}
              <TableHeader>{t('admin.kitchen.columns.firedAt')}</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.tickets.map(({ ticket }) => (
              <TableRow key={ticket.id}>
                <TableCell>
                  <CarbonLink as={Link} to={`/kitchen/tickets/${ticket.id}`}>
                    {`${ticket.stationCode} #${ticket.ticketNumber}`}
                  </CarbonLink>
                </TableCell>
                <TableCell>
                  <KitchenStatusTag kind="ticket" status={ticket.status} />
                </TableCell>
                {mode === 'store' && (
                  <TableCell>
                    <PrintStatus ticket={ticket} />
                  </TableCell>
                )}
                <TableCell>{format.dateTime(ticket.firedAt)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}
